import { getAPIBaseUrl } from '@/app/platform/core/env.ts';
import { tokenManager } from '@/app/platform/core/authentication/tokenManager.ts';
import { formatApiDetail } from '@/app/platform/core/authentication/apiDetail.ts';
import { buildTechnicalDetail, setSessionEndReason } from '@/app/platform/core/authentication/sessionEndReason.ts';

class ApiService {
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = tokenManager.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit,
    isRetry = false,
  ): Promise<T> {
    const url = `${getAPIBaseUrl()}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options?.headers,
        },
      });

      if (response.status === 401) {
        const failureDetail: string | null = await response
          .json()
          .then((d) => formatApiDetail(d?.detail))
          .catch(() => null);

        if (!isRetry) {
          const newToken = await tokenManager.tryRefresh();
          if (newToken) {
            return this.request<T>(endpoint, options, true);
          }
        }
        // A failed refresh only means the session is dead if it actually got cleared (a
        // confirmed 401/403 rejection from the refresh endpoint) — a transient failure
        // (network error, 5xx during e.g. a redeploy) leaves tokens in place, so this
        // request should just fail rather than force the user to log back in.
        if (!tokenManager.getRefreshToken()) {
          // keepExisting: a refresh attempt just above may already have recorded the more
          // specific reason the *refresh itself* was rejected for (see AuthContext's
          // doRefresh) — this is only the fallback when no refresh was attempted at all.
          setSessionEndReason(
            failureDetail || 'Your session is no longer valid.',
            true,
            buildTechnicalDetail(`${options?.method ?? 'GET'} ${endpoint}`, 401, null),
          );
          window.location.replace('/auth/login');
          return new Promise<T>(() => {});
        }
        throw new Error('Session temporarily unavailable — please try again.');
      }

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ detail: 'An error occurred' }));
        const apiError: Error & { status?: number } = new Error(
          formatApiDetail(error.detail) || `HTTP error! status: ${response.status}`,
        );
        apiError.status = response.status;
        throw apiError;
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null as T;
      }

      return await response.json();
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async getBlob(endpoint: string): Promise<Blob> {
    const url = `${getAPIBaseUrl()}${endpoint}`;
    const response = await fetch(url, { headers: this.getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiService = new ApiService();
